import { combineEpics } from "redux-observable";
import { from, of } from "rxjs";
import {
    catchError,
    filter,
    groupBy,
    ignoreElements,
    map,
    mergeMap,
    switchMap,
    tap,
} from "rxjs/operators";

import { Action, actionCreators, isOfAction, isOfActions } from "../actions";
import { getUpdateExpiration } from "../reducers/config";
import { getPeristent } from "../reducers/persist";
import {
    getJoinedRoomHistory,
    getRoomViewEditMode,
    getRoomViewIsOpen,
    LOAD_ROOM_HISTORY_MAX_PAGE_SIZE,
    LOAD_ROOM_HISTORY_PAGE_SIZE,
    RoomType,
} from "../reducers/rooms";
import { getMentionsMultiMap } from "../reducers/roster";
import { hasMeta } from "../service/Data";
import { notUndefined } from "../utils/any";
import { executeOperation } from "../utils/effects";
import { max_ts } from "../utils/proto";
import { Epic } from "../utils/types";

const onOpen: Epic = (action$, state$) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.openRoomSucceeded)),
        map(({ payload: { roomView } }) => {
            const { historyAroundTs, type } = roomView;
            if (type === RoomType.ACTIVITY_LOG) {
                return actionCreators.loadRoomHistoryRequested({
                    roomView,
                    how: "lt",
                    ts: max_ts,
                });
            } else if (historyAroundTs) {
                return actionCreators.loadRoomHistoryRequested({
                    roomView,
                    how: "around",
                    ts: historyAroundTs,
                });
            }

            return undefined;
        }),
        filter(notUndefined)
    );
};

const loadRoomHistory: Epic = (action$, state$, { App }) => {
    return action$.pipe(
        filter(
            isOfActions([
                actionCreators.loadRoomHistoryRequested,
                actionCreators.loadJoinedRoomHistoryRequested,
            ])
        ),
        groupBy(
            ({
                payload: {
                    roomView: { viewId },
                },
            }) => viewId
        ),
        mergeMap(perRoomAction$ =>
            perRoomAction$.pipe(
                switchMap(({ type, payload: { roomView, how, ts } }) => {
                    const { roomId } = roomView;
                    return executeOperation(() => {
                        if (roomView.type === RoomType.ACTIVITY_LOG) {
                            return App.proto.loadRoomLogs(
                                how === "gt"
                                    ? {
                                          room_id: roomId,
                                          gt_ts: ts,
                                      }
                                    : {
                                          room_id: roomId,
                                          lt_ts: ts,
                                      }
                            );
                        } else {
                            return App.proto.loadRoomHistory(
                                how === "around"
                                    ? {
                                          room_id: roomId,
                                          around_ts: ts,
                                          page_size: LOAD_ROOM_HISTORY_MAX_PAGE_SIZE,
                                      }
                                    : how === "gt"
                                    ? {
                                          room_id: roomId,
                                          gt_ts: ts,
                                          page_size: LOAD_ROOM_HISTORY_PAGE_SIZE,
                                      }
                                    : {
                                          room_id: roomId,
                                          lt_ts: ts,
                                          page_size: LOAD_ROOM_HISTORY_PAGE_SIZE,
                                      },
                                { tag: `[history] ${how} ${ts}` }
                            );
                        }
                    }).pipe(
                        mergeMap(({ result$ }) =>
                            result$.pipe(
                                map(data => {
                                    const payload = { roomView, how, data };
                                    if (type === actionCreators.loadRoomHistoryRequested.type) {
                                        return actionCreators.loadRoomHistory(payload);
                                    } else {
                                        return actionCreators.loadJoinedRoomHistory(payload);
                                    }
                                }),
                                catchError((err, retry) => {
                                    if (getRoomViewIsOpen(state$.value, roomView)) {
                                        return retry;
                                    }
                                    return of<never>();
                                })
                            )
                        ),
                        catchError(err => {
                            console.error("crash in load history", roomView, err);
                            return of(actionCreators.closeRoomRequested([roomView]));
                        })
                    );
                })
            )
        )
    );
};

const onSetEditMode: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.setRoomViewEditMode)),
        tap(({ payload: { roomView, message } }) =>
            App.actionCreators.RoomInput(roomView.roomId, message.raw || message.parsed)
        ),
        ignoreElements()
    );

const onUnsetEditMode: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.unsetRoomViewEditMode)),
        tap(({ payload: { roomView } }) => App.actionCreators.RoomInput(roomView.roomId, "")),
        ignoreElements()
    );

const onSendRoomMessage: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.sendRoomMessage)),
        mergeMap(({ roomView, pendingId, raw, uploads = [], monolog }) =>
            from(
                App.proto.sendRoomText(
                    { room_id: roomView.roomId, raw, uploads },
                    getMentionsMultiMap(state$.value),
                    { uuid: pendingId }
                )
            ).pipe(
                mergeMap(data => {
                    const actions: Action[] = [actionCreators.onRoomText({ data, pendingId })];
                    const monologUserId = monolog && monolog.userId;
                    if (monologUserId) {
                        actions.push(
                            actionCreators.setReceiptsEnqueued({
                                roomId: roomView.roomId,
                                ts: data.ts,
                                monologUserId,
                            })
                        );
                    }
                    return actions;
                }),
                catchError(error => {
                    console.error("Failed to send message", roomView.roomId, raw, error);
                    return [actionCreators.sendRoomMessageFailed(roomView, pendingId)];
                })
            )
        )
    );

const onSendRoomMessageFailed: Epic = (action$, state$) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.sendRoomMessageFailed)),
        mergeMap(({ pendingId, roomView }) => {
            const message = getJoinedRoomHistory(state$.value, roomView).find(
                y => y.id === pendingId
            );
            const { failedMessages: old } = getPeristent(state$.value);
            if (message) {
                const { roomId } = roomView;
                const failedMessages = old
                    .filter(({ id }) => id !== message.id)
                    .concat({
                        roomId,
                        id: message.id,
                        text: (hasMeta(message) && message.__meta__.pendingRaw) || message.parsed,
                        author_user: message.author_user,
                    });
                return of(actionCreators.storeObject({ failedMessages }));
            }
            return of<never>();
        })
    );
};

const onDiscardFailedRoomMessage: Epic = (action$, state$) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.discardFailedRoomMessage)),
        mergeMap(({ pendingId }) => {
            const { failedMessages: old } = getPeristent(state$.value);
            const failedMessages = old.filter(({ id }) => id !== pendingId);
            return of(actionCreators.storeObject({ failedMessages }));
        })
    );
};

const onUpdateRoomMessage: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.updateRoomMessage)),
        mergeMap(({ roomView, messageId, raw }) => {
            return from(
                App.proto.updateRoomText(
                    {
                        id: messageId,
                        room_id: roomView.roomId,
                        raw,
                    },
                    getMentionsMultiMap(state$.value),
                    { tag: "edit message" }
                )
            ).pipe(
                mergeMap(data => {
                    const actions: Action[] = [actionCreators.onUpdateRoomText({ data })];
                    const editMode = getRoomViewEditMode(state$.value, roomView.viewId, messageId);
                    if (editMode) {
                        actions.push(actionCreators.unsetRoomViewEditMode({ roomView }));
                    }
                    return of(...actions);
                }),
                catchError(e => {
                    const updateExpired = e && e.code === "message_update_expired";
                    if (!updateExpired) {
                        console.error("Failed to update message", roomView.roomId, messageId);
                    }
                    const editMode = getRoomViewEditMode(state$.value, roomView.viewId, messageId);
                    if (!editMode) {
                        return of<never>();
                    }
                    return of(
                        actionCreators.updateRoomViewEditMode({
                            roomView,
                            editMode: {
                                ...editMode,
                                error: true,
                                sending: false,
                                expired: updateExpired,
                            },
                        })
                    );
                })
            );
        })
    );

const onDeleteRoomMessage: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.deleteRoomMessage)),
        mergeMap(({ roomView, messageId }) => {
            return from(
                App.proto.deleteRoomText(
                    { room_id: roomView.roomId, id: messageId },
                    { tag: "delete message" }
                )
            ).pipe(
                map(data => App.actionCreators.onDeleteRoomText({ data })),
                catchError(error => {
                    if (error && error.code === "message_delete_expired") {
                        return [
                            App.actionCreators.onDeleteTooLateError({
                                roomId: roomView.roomId,
                                expiration: getUpdateExpiration(state$.value),
                            }),
                        ];
                    }
                    console.error("Failed to delete message", roomView.roomId, messageId, error);
                    return of<never>();
                })
            );
        })
    );

const onRoomText: Epic = (action$, state$) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.onRoomText)),
        mergeMap(({ payload: { pendingId } }) => {
            const { failedMessages: old } = getPeristent(state$.value);
            const failedMessages = old.filter(({ id }) => id !== pendingId);
            return of(actionCreators.storeObject({ failedMessages }));
        })
    );
};

export const roomsRealtimeEpic = combineEpics(
    onOpen,
    loadRoomHistory,
    onSetEditMode,
    onUnsetEditMode,
    onSendRoomMessage,
    onSendRoomMessageFailed,
    onDiscardFailedRoomMessage,
    onUpdateRoomMessage,
    onDeleteRoomMessage,
    onRoomText
);
